import { type ReactNode } from 'react';
import { Spin } from 'antd';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { selectRiaProfile } from '@/store/features/userinfo/userinfoSlice';

type ApexOnlyRouteProps = {
  children: ReactNode;
  fallbackPath?: string;
};

const RISK_RETAKE_PATH = '/wealth/open-brokerage-account/step1';
const RISK_RETAKE_SEARCH_PARAM = 'retakeRiskOnly';

const ApexOnlyRoute = ({ children, fallbackPath = '/home/wealth' }: ApexOnlyRouteProps) => {
  const riaProfile = useAppSelector(selectRiaProfile);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  // Profile-level RTQ reuses the brokerage questionnaire without Apex.
  // Only skip the Apex lookup on that path so leftover/crafted route state or
  // query params cannot open deposit, withdraw, transfer, or KYC.
  const retakeRiskOnly =
    location.pathname === RISK_RETAKE_PATH &&
    (Boolean((location.state as { retakeRiskOnly?: boolean } | null)?.retakeRiskOnly) ||
      searchParams.get(RISK_RETAKE_SEARCH_PARAM) === 'true');
  const riaProfileLoading = riaProfile.profile === null;
  const apexAvailable =
    Boolean(riaProfile.profile?.apex_correspondent_id) &&
    Boolean(riaProfile.features?.apex_enabled);

  if (!retakeRiskOnly && riaProfileLoading) {
    return (
      <div data-testid="apex-route-loading" aria-busy="true">
        <Spin size="large" />
      </div>
    );
  }

  if (!retakeRiskOnly && !apexAvailable) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default ApexOnlyRoute;
